<template>
  <span>
    <template v-if="openRequest">
      <b-badge variant="warning" class="mr-2">
        Pengajuan {{ actionLabel(openRequest.action) }} {{ isReturned ? 'dikembalikan untuk diperbaiki' : 'sedang direview' }}
      </b-badge>
      <span v-if="openRequest.request_note" class="text-muted mr-2">{{ openRequest.request_note }}</span>
      <span v-if="isReturned && openRequest.review_note" class="text-muted mr-2">Alasan: {{ openRequest.review_note }}</span>
      <b-button v-if="isRequester && isReturned" variant="primary" class="mt-0 mr-2" :disabled="isLoading" @click="fixReturnedRequest">Perbaiki Pengajuan</b-button>
      <b-button v-if="isRequester" variant="secondary" class="mt-0" :disabled="isLoading" @click="cancelRequest">Batalkan Pengajuan</b-button>
      <!-- Keputusan dapat diambil di sini, tidak harus lewat halaman Review. -->
      <template v-if="canDecide">
        <b-button variant="success" class="mt-0 mr-2" :disabled="isLoading" @click="openDecision('approve')">Setujui</b-button>
        <b-button variant="warning" class="mt-0 mr-2" :disabled="isLoading" @click="openDecision('return')">Kembalikan</b-button>
        <b-button variant="danger" class="mt-0" :disabled="isLoading" @click="openDecision('reject')">Tolak</b-button>
      </template>
    </template>
    <template v-else-if="canRequest">
      <b-button v-if="changeFields.length" variant="warning" class="mt-0 mr-2" :disabled="isLoading" @click="openChange">Ajukan Perubahan</b-button>
      <b-button v-if="reopenable" variant="warning" class="mt-0 mr-2" :disabled="isLoading" @click="openReopen">Ajukan Perubahan</b-button>
      <b-button variant="danger" class="mt-0" :disabled="isLoading" @click="openDelete">Ajukan Hapus</b-button>
    </template>

    <b-modal ref="decisionModal" :title="decisionTitle" :ok-title="decisionTitle" cancel-title="Batal" @ok="submitDecision">
      <template v-if="openRequest">
        <p class="text-muted">
          Pengajuan {{ actionLabel(openRequest.action) }} oleh {{ openRequest.requester ? openRequest.requester.name : '-' }}.
          <span v-if="openRequest.request_note"><br>Catatan staff: {{ openRequest.request_note }}</span>
        </p>
        <div v-if="openRequest.action === 'update' && openRequest.proposed_snapshot" class="table-responsive mb-3">
          <table class="table table-sm">
            <thead><tr><th>Field</th><th>Nilai Lama</th><th>Usulan Baru</th></tr></thead>
            <tbody>
              <tr v-for="field in proposedFields" :key="field">
                <td>{{ fieldLabel(field) }}</td>
                <td>{{ oldValue(field) }}</td>
                <td><strong>{{ openRequest.proposed_snapshot[field] }}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
        <b-alert :show="decisionMode === 'approve' && openRequest.action === 'reopen'" variant="warning">
          Stok transaksi ini akan dikembalikan dan transaksinya menjadi draft. Nomor transaksi tidak berubah.
        </b-alert>
        <b-alert :show="decisionMode === 'approve' && openRequest.action === 'delete'" variant="danger">
          Data ini akan dihapus. Untuk penjualan, stoknya dikembalikan lewat mutasi tercatat.
        </b-alert>
        <label class="form-label mb-1">Catatan Anda</label>
        <b-form-textarea v-model="reviewNote" :placeholder="decisionMode === 'approve' ? 'Opsional' : 'Wajib diisi'" />
      </template>
    </b-modal>

    <b-modal ref="changeModal" title="Ajukan Perubahan" ok-title="Kirim Pengajuan" cancel-title="Batal" @ok="submitChange">
      <p class="text-muted">
        Data lama tetap dipakai sampai perubahan ini disetujui.
      </p>
      <div v-for="field in changeFields" :key="field.key" class="mb-2">
        <label class="form-label mb-1">{{ field.label }}</label>
        <div class="text-muted mb-1" style="font-size: 80%;">Nilai sekarang: {{ displayValue(field) }}</div>
        <b-form-input
          v-model="proposed[field.key]"
          :type="field.type || 'text'"
          :inputmode="field.inputmode || null" />
      </div>
      <div class="mt-3">
        <label class="form-label mb-1">Catatan</label>
        <b-form-textarea v-model="requestNote" placeholder="Alasan perubahan" />
      </div>
    </b-modal>

    <b-modal ref="reopenModal" title="Ajukan Perubahan" ok-title="Kirim Pengajuan" cancel-title="Batal" @ok="submitReopen">
      <p>
        Transaksi tetap final sampai pengajuan ini disetujui. Setelah disetujui,
        stoknya dikembalikan dan transaksi kembali menjadi draft agar dapat
        diperbaiki, dengan nomor transaksi yang tetap sama.
      </p>
      <label class="form-label mb-1">Catatan</label>
      <b-form-textarea v-model="requestNote" placeholder="Alasan perubahan" />
    </b-modal>

    <b-modal ref="deleteModal" title="Ajukan Hapus" ok-title="Kirim Pengajuan" cancel-title="Batal" :ok-disabled="isLoading" @ok="submitDelete">
      <p>Data tetap aktif sampai pengajuan hapus ini disetujui.</p>
      <label class="form-label mb-1">Catatan</label>
      <b-form-textarea v-model="requestNote" placeholder="Alasan penghapusan" />
    </b-modal>
  </span>
</template>

<script>
import ApprovalRequest from '@/models/ApprovalRequest.js'
import { Roles } from '@/_helpers'

const approvalRequestModel = new ApprovalRequest()

export default {
  props: {
    targetType: {
      type: String,
      required: true
    },
    targetId: {
      type: [String, Number],
      required: true
    },
    // Daftar field yang boleh diusulkan berubah. Kosong berarti hanya
    // pengajuan hapus yang tersedia.
    changeFields: {
      type: Array,
      default: () => []
    },
    currentValues: {
      type: Object,
      default: () => ({})
    },
    createdBy: {
      type: [String, Number],
      default: null
    },
    // Transaksi diperbaiki dengan cara dibuka kembali menjadi draft, bukan
    // dengan mengusulkan nilai baru satu per satu.
    reopenable: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isLoading: false,
      requestNote: '',
      proposed: {},
      openRequest: null,
      reviewNote: '',
      decisionMode: 'approve'
    }
  },
  computed: {
    isRequester () {
      return this.openRequest &&
        Number(this.openRequest.requested_by) === Number(this.$store.getters['account/accountData'].id)
    },
    isReturned () {
      return this.openRequest && this.openRequest.status === 'returned'
    },
    canDecide () {
      // Pengaju tidak boleh memutuskan pengajuannya sendiri.
      return this.openRequest &&
        this.$store.getters['account/accountData'].role === Roles.ADMIN &&
        !this.isRequester && !this.isReturned
    },
    canRequest () {
      const account = this.$store.getters['account/accountData']
      // Stok master dipakai bersama oleh semua staff. Staff boleh mengusulkan
      // perubahan/penghapusan stok final; keputusan tetap berada di admin.
      const canRequestStock = this.targetType === 'stock' && account.role === Roles.STAFF
      return account.role === Roles.ADMIN || canRequestStock || Number(this.createdBy) === Number(account.id)
    },
    decisionTitle () {
      return { approve: 'Setujui', return: 'Kembalikan', reject: 'Tolak' }[this.decisionMode]
    },
    proposedFields () {
      return this.openRequest && this.openRequest.proposed_snapshot
        ? Object.keys(this.openRequest.proposed_snapshot)
        : []
    }
  },
  mounted () {
    this.loadOpenRequest()
  },
  methods: {
    async loadOpenRequest () {
      try {
        this.openRequest = await approvalRequestModel.findOpenFor(this.targetType, this.targetId)
      } catch (e) {
        this.openRequest = null
      }
    },
    cancelRequest () {
      return this.run(
        () => approvalRequestModel.cancel(this.openRequest.id),
        'Pengajuan dibatalkan.'
      )
    },
    displayValue (field) {
      const value = this.currentValues[field.key]
      return value === null || value === undefined || value === '' ? '-' : value
    },
    openChange () {
      this.requestNote = ''
      const proposed = {}
      this.changeFields.forEach(field => {
        proposed[field.key] = this.isReturned && this.openRequest.proposed_snapshot
          ? this.openRequest.proposed_snapshot[field.key]
          : this.currentValues[field.key]
      })
      this.proposed = proposed
      this.$refs.changeModal.show()
    },
    fieldLabel (field) {
      const known = this.changeFields.find(f => f.key === field)
      return known ? known.label : field
    },
    oldValue (field) {
      const snapshot = (this.openRequest && this.openRequest.current_snapshot) || {}
      const value = snapshot[field]
      return value === null || value === undefined || value === '' ? '-' : value
    },
    openDecision (mode) {
      this.decisionMode = mode
      this.reviewNote = ''
      this.$refs.decisionModal.show()
    },
    submitDecision () {
      if (this.decisionMode !== 'approve' && !this.reviewNote) {
        this.$store.dispatch('notification/error', 'Catatan alasan wajib diisi.')
        return
      }
      const id = this.openRequest.id
      const mode = this.decisionMode
      const note = this.reviewNote
      return this.run(() => {
        if (mode === 'approve') return approvalRequestModel.approve(id, note)
        if (mode === 'return') return approvalRequestModel.returnForRevision(id, note)
        return approvalRequestModel.reject(id, note)
      }, 'Keputusan tersimpan.')
    },
    actionLabel (action) {
      return { update: 'perubahan', reopen: 'perubahan', delete: 'hapus' }[action] || action
    },
    openReopen () {
      this.requestNote = this.isReturned && this.openRequest ? this.openRequest.request_note : ''
      this.$refs.reopenModal.show()
    },
    submitReopen () {
      return this.run(() => {
        if (this.isReturned) return approvalRequestModel.resubmit(this.openRequest.id, { request_note: this.requestNote })
        return approvalRequestModel.requestReopen(this.targetType, this.targetId, this.requestNote)
      }, 'Pengajuan perubahan dikirim ke review.')
    },
    openDelete () {
      this.requestNote = this.isReturned && this.openRequest ? this.openRequest.request_note : ''
      this.$refs.deleteModal.show()
    },
    async run (action, successMessage) {
      if (this.isLoading) return
      this.isLoading = true
      try {
        await action()
        this.$store.dispatch('notification/success', successMessage)
        await this.loadOpenRequest()
        this.$emit('submitted')
      } catch (e) {
        this.$store.dispatch('notification/error', e)
      }
      this.isLoading = false
    },
    submitChange () {
      const changed = {}
      this.changeFields.forEach(field => {
        const normalized = this.normalizeChangeValue(field, this.proposed[field.key])
        if (normalized === null) {
          return
        }
        if (String(normalized) !== String(this.currentValues[field.key])) {
          changed[field.key] = normalized
        }
      })
      const invalidWeight = this.changeFields.some(field => {
        return field.key === 'berat' && this.normalizeChangeValue(field, this.proposed[field.key]) === null
      })
      if (invalidWeight) {
        this.$store.dispatch('notification/error', 'Berat harus berupa angka, contoh: 5.79.')
        return
      }
      if (!Object.keys(changed).length) {
        this.$store.dispatch('notification/error', 'Tidak ada nilai yang diubah.')
        return
      }
      return this.run(() => {
        if (this.isReturned) return approvalRequestModel.resubmit(this.openRequest.id, { proposed_snapshot: changed, request_note: this.requestNote })
        return approvalRequestModel.requestChange(this.targetType, this.targetId, changed, this.requestNote)
      }, 'Pengajuan perubahan dikirim ke review.')
    },
    normalizeChangeValue (field, value) {
      if (field.key !== 'berat') return value
      const normalized = String(value === null || value === undefined ? '' : value).trim().replace(',', '.')
      if (!/^\d+(?:\.\d+)?$/.test(normalized) || Number(normalized) <= 0) return null
      return Number(normalized)
    },
    submitDelete () {
      return this.run(() => {
        if (this.isReturned) return approvalRequestModel.resubmit(this.openRequest.id, { request_note: this.requestNote })
        return approvalRequestModel.requestDelete(this.targetType, this.targetId, this.requestNote)
      }, 'Pengajuan hapus dikirim ke review.')
    },
    fixReturnedRequest () {
      if (this.openRequest.action === 'update') return this.openChange()
      if (this.openRequest.action === 'reopen') return this.openReopen()
      return this.openDelete()
    }
  }
}
</script>
